<template>
  <v-card flat>
    <v-card-title class="title" v-if="title">
      <h4 class="text-truncate">
        {{ title }}
      </h4>
      <v-spacer />
      <slot name="headerActions" v-if="!subtitle" />
      <v-btn icon class="mr-4" @click="loadAllWrapper" :loading="loadingAll" v-if="loadAll">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-subtitle v-if="subtitle">
      {{ subtitle }}
      <slot name="headerActions" />
    </v-card-subtitle>
    <v-card :outlined="outlined" flat :class="margin && !$vuetify.breakpoint.smAndDown ? 'ma-2' : ''">
      <v-card-title class="title pt-4" v-if="!hideTableHeader">
        <v-text-field
          v-if="!hideSearch"
          dense
          class="searchField"
          v-model="search"
          append-icon="mdi-magnify"
          :label="i18n['search']"
          single-line
          hide-details
          outlined
        ></v-text-field>
        <slot v-else name="search" />
        <v-spacer></v-spacer>

        <context-menu v-if="$vuetify.breakpoint.smAndDown">
          <slot v-if="isSelected" name="selectActions" :selected="selected" v-bind="selected" />
          <slot name="bodyActions" />
        </context-menu>
        <slot
          v-if="!$vuetify.breakpoint.smAndDown && isSelected"
          name="selectActions"
          :selected="selected"
          v-bind="selected"
        />
        <slot v-if="!$vuetify.breakpoint.smAndDown" name="bodyActions" />

        <v-btn
          outlined
          class="ma-1"
          @click="newItems = []"
          v-if="newItems.length > 0 && !loadingCreation && isDropEnabled"
          color="error"
        >
          {{ i18n["drop"] }}
        </v-btn>
        <v-btn
          outlined
          class="ma-1"
          @click="confirmNewItemsWrapper"
          v-if="newItems.length > 0 && !loadingCreation && isConfirmEnabled"
          color="success"
        >
          {{ i18n["confirm"] }}
        </v-btn>
        <v-btn
          elevation="0"
          color="info"
          class="ma-1"
          @click="addItem"
          :loading="loadingCreation"
          v-if="addItem && confirmNewItems"
        >
          {{ i18n["add"] }}
        </v-btn>
      </v-card-title>
      <v-divider v-if="!hideTableHeader" />
      <v-data-table
        ref="dataTable"
        v-model="selected"
        :loading="loadingAll || loading"
        :loading-text="i18n['loading']"
        :search="search"
        :headers="headers"
        :items="items"
        :hide-default-footer="hideTableFooter"
        :disable-pagination="disablePagination"
        :page.sync="pageLocal"
        :items-per-page="itemsPerPage"
        :single-expand="false"
        :show-select="showSelect"
        :item-key="itemKey"
        :class="`${$vuetify.breakpoint.smAndDown ? '' : 'mt-4'} ${isClickableRow ? 'row-pointer' : ''}`"
        :dense="dense"
        :sort-by.sync="sortByLocal"
        :sort-desc.sync="sortDescLocal"
        :footer-props="{ itemsPerPageText: i18n['itemsPerPageText'] }"
        :customFilter="customFilter"
        :single-select="singleSelect"
        :custom-group="customGroup"
        :groupBy="groupBy"
        :groupDesc="groupDesc"
        @click:row="(e) => $emit('click:row', e)"
        @current-items="currentItems = $event"
        :style="hasClickRowListener ? 'cursor: pointer;' : ''"
        :item-class="itemClass"
      >
        <template
          v-if="isMobile"
          v-slot:item="{ expand, index, item, isExpanded, isMobile, isSelected, select, headers, on, attrs }"
        >
          <tr @click="$emit('click:row', item)">
            <td :colspan="headers.length" style="width: 100vw">
              <div style="display: flex; align-items: center; justify-content: center" class="ml-3 my-2">
                <div v-if="showSelect">
                  <v-checkbox
                    v-if="isSelected"
                    :value="true"
                    :disabled="!isSelectableItem(item)"
                    @click.stop="select(false)"
                  />
                  <v-checkbox v-else :value="false" :disabled="!isSelectableItem(item)" @click.stop="select(true)" />
                </div>
                <v-spacer />
                <slot name="controlsActions" :item="item" class="mt-1">
                  <span v-if="isNewItem(item)">
                    <v-btn icon @click.stop="removeItem(item)">
                      <v-icon>mdi-trash-can</v-icon>
                    </v-btn>
                  </span>
                  <span v-else-if="visibleControlElements(item).length">
                    <tooltip
                      :text="controlTooltip(visibleControlElements(item)[0], item)"
                      :disabled="!controlTooltip(visibleControlElements(item)[0], item)"
                    >
                      <v-btn
                        icon
                        @click.stop="visibleControlElements(item)[0].action(item)"
                        :loading="isControlLoading(visibleControlElements(item)[0], item)"
                        :disabled="isControlDisabled(visibleControlElements(item)[0], item)"
                        :aria-label="controlLabel(visibleControlElements(item)[0], item)"
                      >
                        <v-icon>{{ visibleControlElements(item)[0].icon }}</v-icon>
                      </v-btn>
                    </tooltip>
                    <span v-if="visibleControlElements(item).length > 1">
                      <v-menu :nudge-bottom="-14" :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="(controlElement, index) in visibleControlElements(item)"
                            :key="index"
                            @click="controlElement.action(item)"
                            :disabled="isControlDisabled(controlElement, item)"
                          >
                            <v-list-item-title v-text="controlElement.text" />
                            <v-list-item-icon>
                              <v-btn
                                icon
                                :loading="isControlLoading(controlElement, item)"
                                :disabled="isControlDisabled(controlElement, item)"
                              >
                                <v-icon>{{ controlElement.icon }}</v-icon>
                              </v-btn>
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </span>
                  </span>
                </slot>
              </div>
              <slot
                name="item"
                :expand="expand"
                :index="index"
                :item="item"
                :isExpanded="isExpanded"
                :isMobile="isMobile"
                :isSelected="isSelected"
                :select="select"
                :headers="headers"
                :on="on"
                :attrs="attrs"
              >
                <div v-for="(header, index) of headers" :key="'header' + index" :elevation="0" class="ml-2 mr-1 my-1">
                  <div class="v-data-table__mobile-row__header ml-2 mr-1" v-if="header.text">
                    {{ header.text }}
                  </div>
                  <div style="display: flex">
                    <v-spacer />
                    <span v-bind:key="header.value">
                      <span v-if="!isNewItem(item) && !Boolean(header.forceEditField)">
                        <slot
                          v-if="header.value"
                          :name="`item.${header.value}`"
                          :item="item"
                          :value="getItemValue(item, header)"
                        >
                          <span v-if="getItemValue(item, header) && header.type === 'date'">
                            {{ simpleDoubleDigitDate(getItemValue(item, header)) }}
                          </span>
                          <span v-else>
                            <span v-for="value of toArray(getItemValue(item, header))" :key="value + Math.random()">
                              {{ value }}
                            </span>
                          </span>
                        </slot>
                      </span>
                      <span v-else-if="header.isDisplay"></span>
                      <span v-else>
                        <v-text-field
                          dense
                          :value="getItemValue(item, header)"
                          @change="(v) => setItemValue(item, header.value, v)"
                          :placeholder="header.placeholder"
                          :rules="header.rules"
                          :type="header.type"
                          rounded
                          filled
                          class="mb-n5 mt-2 pt-2 pb-4"
                        ></v-text-field>
                      </span>
                    </span>
                  </div>
                </div>
              </slot>
            </td>
          </tr>
        </template>
        <template v-slot:group.header="{ groupBy, group, toggle, isOpen, items, headers, remove, isMobile }">
          <!-- SLOT TO OVERWRITE GROUP BY-->
          <v-hover v-slot="{ hover }">
            <td :colspan="headers.length">
              <debug> headers.length: {{ headers.length }} </debug>
              <debug> groupBy: {{ groupBy }} </debug>
              <debug> group: {{ group }} </debug>
              <slot
                name="group.header"
                :groupBy="groupBy"
                :group="group"
                :toggle="toggle"
                :isOpen="isOpen"
                :items="items"
                :headers="headers"
                :remove="remove"
                :isMobile="isMobile"
                :hover="hover"
              >
                <div style="display: flex; align-items: center" @click="toggle">
                  <b> {{ group }}</b>
                  <v-icon v-if="isOpen">mdi-chevron-down</v-icon>
                  <v-icon v-else>mdi-chevron-right</v-icon>
                  <v-spacer />
                  <v-btn v-if="!disableGroupClosing && hover" icon @click.stop="remove">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </slot>
            </td>
          </v-hover>
        </template>

        <template slot="no-data">
          <slot name="no-data">
            <latest-entries-card-empty class="my-8" />
          </slot>
        </template>
        <template slot="body.prepend">
          <slot name="filter"></slot>
        </template>
        <template v-for="header of headers" :slot="`item.${header.value}`" slot-scope="{ item }">
          <span v-bind:key="header.value">
            <debug> slotname: item.{{ header.value }} </debug>
            <span v-if="!isNewItem(item) && !Boolean(header.forceEditField)">
              <slot :name="`item.${header.value}`" :item="item" :value="getItemValue(item, header)" v-if="header.value">
                <span v-if="getItemValue(item, header) && header.type === 'date'">
                  {{ simpleDoubleDigitDate(getItemValue(item, header)) }}
                </span>
                <span v-else>
                  <span v-for="value of toArray(getItemValue(item, header))" :key="value + Math.random()">
                    {{ value }}
                  </span>
                </span>
              </slot>
            </span>
            <span v-else-if="header.isDisplay"></span>
            <span v-else>
              <v-text-field
                dense
                :value="getItemValue(item, header)"
                @change="(v) => setItemValue(item, header.value, v)"
                :placeholder="header.placeholder"
                :rules="header.rules"
                :type="header.type"
                rounded
                filled
                class="mb-n5 mt-2 pt-2 pb-4"
              ></v-text-field>
            </span>
          </span>
        </template>
        <!--
          Vuetify's own select cell, plus `isItemSelectable`. It has to stay a `v-simple-checkbox`: that one
          stops the click, where a `v-checkbox` lets it through to the row, so selecting a row would open it too.
        -->
        <!--
          Vuetify's select-all also picks the items `isItemSelectable` disables, so with a predicate the header
          checkbox handles only the selectable ones.
        -->
        <template v-if="showSelect && isItemSelectable" v-slot:header.data-table-select="{ props }">
          <v-simple-checkbox
            class="v-data-table__checkbox"
            :color="props.color"
            :value="isEverySelectableSelected"
            :indeterminate="isSomeSelectableSelected && !isEverySelectableSelected"
            :disabled="selectablePageItems.length === 0"
            @input="toggleSelectableItems"
          />
        </template>
        <template v-if="showSelect" v-slot:item.data-table-select="{ item, isSelected, select }">
          <v-simple-checkbox
            class="v-data-table__checkbox"
            :value="isSelected"
            :disabled="!isSelectableItem(item)"
            @input="select"
          />
        </template>
        <template v-slot:item.controls="{ item }">
          <slot name="controlsActions" :item="item">
            <span v-if="isNewItem(item)">
              <v-btn icon @click.stop="removeItem(item)">
                <v-icon>mdi-trash-can</v-icon>
              </v-btn>
            </span>
            <span v-else-if="visibleControlElements(item).length">
              <tooltip
                :text="controlTooltip(visibleControlElements(item)[0], item)"
                :disabled="!controlTooltip(visibleControlElements(item)[0], item)"
              >
                <v-btn
                  icon
                  @click.stop="visibleControlElements(item)[0].action(item)"
                  :loading="isControlLoading(visibleControlElements(item)[0], item)"
                  :disabled="isControlDisabled(visibleControlElements(item)[0], item)"
                  :aria-label="controlLabel(visibleControlElements(item)[0], item)"
                >
                  <v-icon>{{ visibleControlElements(item)[0].icon }}</v-icon>
                </v-btn>
              </tooltip>
              <span v-if="visibleControlElements(item).length > 1">
                <v-menu :nudge-bottom="-14" :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(controlElement, index) in visibleControlElements(item)"
                      :key="index"
                      @click="controlElement.action(item)"
                      :disabled="isControlDisabled(controlElement, item)"
                    >
                      <v-list-item-title v-text="controlElement.text" />
                      <v-list-item-icon>
                        <v-btn
                          icon
                          :loading="isControlLoading(controlElement, item)"
                          :disabled="isControlDisabled(controlElement, item)"
                        >
                          <v-icon>{{ controlElement.icon }}</v-icon>
                        </v-btn>
                      </v-list-item-icon>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </span>
            </span>
          </slot>
        </template>
      </v-data-table>
      <slot></slot>
      <template name="footer">
        <slot name="footer" />
      </template>
    </v-card>
  </v-card>
</template>

<script lang="ts">
import { Component, Prop, Watch } from "vue-property-decorator";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { filterJson } from "@/lib/utility/filter";
import LatestEntriesCardEmpty from "../cards/LatestEntriesCardEmpty.vue";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { getArrayedNestedPath, getNestedObjectValues } from "@/lib/objectPath-helper";
import Debug from "@/components/utility/Debug.vue";
import {
  IControlElements as IControlElements2,
  ITableWrapperHeader as ITableWrapperHeader2
} from "@/lib/types/tableWrapper";
import ContextMenu from "./ContextMenu.vue";
import Tooltip from "./tooltip.vue";
import { toArray } from "@/lib/array-helper";

/**
 *  FIXME: Refactoring this is a pain
 * @deprecated use @/lib/types/tableWrapper"
 *
 */
export type IControlElements<T = any> = IControlElements2<T>;

/**
 *  FIXME: Refactoring this is a pain
 * @deprecated use @/lib/types/tableWrapper"
 */
export type ITableWrapperHeader = ITableWrapperHeader2;

@Component({ components: { LatestEntriesCardEmpty, Debug, ContextMenu, Tooltip } })
export default class TableWrapper extends DarkModeHighlightMixin {
  readonly toArray = toArray;

  @Prop({ default: false })
  hideSearch?: boolean;

  @Prop()
  title!: string;

  @Prop({ default: false })
  dense?: boolean;

  @Prop()
  subtitle?: string;

  @Prop()
  loadAll?: Function;

  @Prop()
  confirmNewItems!: Function;

  @Prop({ default: true })
  isConfirmEnabled!: boolean;

  @Prop({ default: true })
  isDropEnabled!: boolean;

  @Prop()
  headers!: ITableWrapperHeader2[];

  @Prop()
  emptyItem!: Record<string, any>;

  @Prop()
  allItems!: Record<string, any>[];

  @Prop()
  sortBy?: string;

  @Prop()
  sortDesc?: boolean;

  @Prop({ default: false })
  hideTableHeader?: boolean;

  @Prop({ default: false })
  hideTableFooter?: boolean;

  @Prop({ default: false })
  disablePagination?: boolean;

  @Prop({ default: false })
  singleSelect?: boolean;

  @Prop({ default: false })
  showSelect?: boolean;

  @Prop({ default: () => [] })
  selectedItems!: Record<string, any>[];

  @Prop()
  controlElements?: IControlElements2[];

  /**
   * Optional predicate to disable the row-select checkbox for individual items.
   */
  @Prop()
  isItemSelectable?: (item: any) => boolean;

  @Prop({ default: false })
  loading?: boolean;

  @Prop({ default: true })
  margin?: boolean;

  @Prop({ default: 15 })
  itemsPerPage?: number;

  @Prop({ default: "id" })
  itemKey!: string;

  @Prop({ default: false })
  isClickableRow!: boolean;

  @Prop()
  itemClass?: string | Function;

  @Prop({})
  groupBy?: string;

  @Prop({ default: false })
  groupDesc?: boolean;

  // When true, group headers cannot be toggled closed/open
  @Prop({ default: false })
  disableGroupClosing?: boolean;

  @Prop()
  page!: number;

  @Prop({ default: true })
  outlined?: boolean;

  @Prop({})
  customGroupMethod?: (items: any[], groupBys: string[], groupDesc: boolean[]) => { name: string; items: any[] }[];

  sortByLocal = "";

  sortDescLocal = false;

  search = "";

  loadingCreation = false;
  loadingAll = false;

  newItems: Record<string, any>[] = [];

  currentItems: Record<string, any>[] = [];

  get hasClickRowListener() {
    return Boolean(this.$listeners && this.$listeners["click:row"]);
  }

  get pageLocal() {
    return this.page;
  }
  set pageLocal(value: number) {
    this.$emit("update:page", value);
  }

  get tableSortDesc() {
    return (this.$refs.dataTable as any)?.sortDesc;
  }

  get tableSortBy() {
    return (this.$refs.dataTable as any)?.sortBy;
  }

  /**
   * Can be used in conjunction with the slot for `selectActions`.
   * A slot can be used like this btn for deletion:
   * ```
   *  <template #selectActions="{selected}">
   *    <v-btn @click="deleteAll(selected)"></v-btn>
   *  </template>
   * ```
   *  The selected items are passed to the slot via `selected` and can be used (as an array) in the function.
   * In above example  all selected items are passed to the deleteAll function.
   */

  get selected() {
    return this.selectedItems;
  }

  set selected(selectedItems: Record<string, any>[]) {
    this.$emit("update:selectedItems", selectedItems);
  }

  get isSelected() {
    return this.selected.length > 0;
  }

  get selectablePageItems() {
    return this.currentItems.filter((item) => this.isSelectableItem(item));
  }

  get isEverySelectableSelected() {
    return this.selectablePageItems.length > 0 && this.selectablePageItems.every((item) => this.isItemSelected(item));
  }

  get isSomeSelectableSelected() {
    return this.selectablePageItems.some((item) => this.isItemSelected(item));
  }

  get items() {
    return [...this.newItems, ...this.allItems];
  }

  get i18n() {
    return this.$t("components.utility.TableWrapper");
  }

  simpleDoubleDigitDate(date: string) {
    return simpleDoubleDigitDate(date);
  }

  @Watch("search")
  emitSearch() {
    this.$emit("updateSearch", this.search);
  }

  customFilter(value: any, search: string, item: any) {
    return filterJson(value, search, item);
  }

  getItemValue(item: any, header: ITableWrapperHeader2) {
    if (header.mapping) {
      return header.mapping(item);
    }
    const found = getArrayedNestedPath(item, header.value);
    if (found.length === 0) return undefined;
    if (found.length === 1) return found[0];
    return found;
  }

  setItemValue(item: any, headerValue: string, value: string) {
    let schema = item;
    const pList = headerValue.split(".");
    const len = pList.length;
    for (let i = 0; i < len - 1; i++) {
      const elem = pList[i];
      if (!schema[elem]) schema[elem] = {};
      schema = schema[elem];
    }

    schema[pList[len - 1]] = value;
  }

  addItem() {
    if (this.emptyItem) {
      this.newItems.push({ ...this.emptyItem, temporaryWorkId: Math.ceil(Math.random() * 1000000) });
    }
    this.$emit("addItem");
  }

  isNewItem(item: Record<string, any>) {
    return Boolean(item.temporaryWorkId);
  }

  async mounted() {
    this.$log.debug(this.itemKey);
    await this.loadAllWrapper();
    this.sortByLocal = this.sortBy ?? "";
    this.sortDescLocal = this.sortDesc ?? false;
  }

  async confirmNewItemsWrapper() {
    try {
      this.loadingCreation = true;
      await this.confirmNewItems(
        this.newItems.map((nI) => {
          return { ...nI, temporaryWorkId: undefined };
        })
      );
      this.newItems = [];
    } catch (e) {
      this.$toast.error((e as any).message);
      this.$log.error(e);
    } finally {
      this.loadingCreation = false;
      await this.loadAllWrapper();
    }
  }

  async loadAllWrapper() {
    try {
      this.loadingAll = true;
      if (this.loadAll) {
        await this.loadAll();
      }
    } catch (e) {
      this.$toast.error((e as any).message);
      this.$log.error(e);
    } finally {
      this.loadingAll = false;
    }
  }

  async removeItem(item: Record<string, any>) {
    const newItemIndex = this.newItems.findIndex((nI) => nI.temporaryWorkId === item.temporaryWorkId);
    this.newItems.splice(newItemIndex, 1);
    this.$emit("removeItem", item);
  }

  customGroup(items: any[], groupBys: string[], groupDesc: boolean[]): { name: string; items: any[] }[] {
    if (this.customGroupMethod) return this.customGroupMethod(items, groupBys, groupDesc);

    const groupMap: Record<string, any[]> = {};

    for (const item of items) {
      for (const groupBy of groupBys) {
        let groups = getArrayedNestedPath(item, groupBy);

        if (!groups.length) {
          groups = [""];
        }

        for (const group of groups) {
          if (!groupMap[group.toString()]) {
            groupMap[group.toString()] = [];
          }

          groupMap[group.toString()].push(item);
        }
      }
    }

    const keys = Object.keys(groupMap).sort((a, b) => (!groupDesc[0] ? a.localeCompare(b) : b.localeCompare(a)));

    return keys.map((key) => {
      return {
        name: key,
        items: groupMap[key]
      };
    });
  }

  isSelectableItem(item: any): boolean {
    return this.isItemSelectable ? this.isItemSelectable(item) : true;
  }

  isItemSelected(item: Record<string, any>) {
    const key = getNestedObjectValues(item, this.itemKey);

    return this.selected.some((selectedItem) => getNestedObjectValues(selectedItem, this.itemKey) === key);
  }

  toggleSelectableItems(select: boolean) {
    const pageKeys = new Set(this.selectablePageItems.map((item) => getNestedObjectValues(item, this.itemKey)));
    const others = this.selected.filter((item) => !pageKeys.has(getNestedObjectValues(item, this.itemKey)));

    this.selected = select ? [...others, ...this.selectablePageItems] : others;
  }

  isControlDisabled(controlElement: IControlElements2, item: any): boolean {
    return typeof controlElement.disabled === "function"
      ? controlElement.disabled(item)
      : controlElement.disabled || false;
  }

  isControlLoading(controlElement: IControlElements2, item: any): boolean {
    return typeof controlElement.loading === "function"
      ? controlElement.loading(item)
      : controlElement.loading || false;
  }

  controlTooltip(controlElement: IControlElements2, item: any): string {
    return (typeof controlElement.tooltip === "function" ? controlElement.tooltip(item) : controlElement.tooltip) || "";
  }

  /**
   * Accessible name for the icon only control button, as the icon alone is not announced by screen readers.
   */
  controlLabel(controlElement: IControlElements2, item: any): string {
    return this.controlTooltip(controlElement, item) || controlElement.text || "";
  }

  isControlHidden(controlElement: IControlElements2, item: any): boolean {
    return typeof controlElement.hide === "function" ? controlElement.hide(item) : controlElement.hide || false;
  }

  visibleControlElements(item: any): IControlElements2[] {
    if (!this.controlElements) return [];
    return this.controlElements.filter((c) => !this.isControlHidden(c, item));
  }
}
</script>

<style lang="css" scoped>
.searchField {
  max-width: 50%;
}

.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>
